/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  FileControllerService,
  ProductControllerService,
  ProductImage,
  ProductImageRequest,
  ProductKitComponent,
  ProductVariant,
  ProductVariantCreateRequest,
  ProductVariantSummary,
  ProductVariantUpdateRequest
} from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { ImageUploadComponent } from '../../image-upload/image-upload.component';

@Component({
  selector: 'app-product-variant-form',
  templateUrl: './product-variant-form.component.html',
  styleUrls: ['./product-variant-form.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class ProductVariantFormComponent
  extends ImageUploadComponent<ProductVariant>
  implements OnInit
{
  @Input()
  product: ProductVariant | null | undefined;

  @Input()
  productId: number | undefined;

  @Output()
  buttonAction = new EventEmitter<ProductVariant | null>();

  @Input()
  images: Array<ProductImage> | undefined;

  @Input()
  productVariants: Array<ProductVariant> | undefined;

  minStock = 5;

  diamanteVermelhoOptions: Array<{ label: string; value: string | null }> = [
    { label: 'Todos', value: null },
    { label: 'Red Diamond', value: 'red diamond' },
    { label: 'Red Diamond Bag', value: 'red diamond bag' },
    { label: 'Red Diamond Box', value: 'red diamond box' },
    { label: 'Red Diamond Combo', value: 'red diamond combo' }
  ];
  tipoEstoqueOptions: Array<{ label: string; value: string | null }> = [
    { label: 'Ambos', value: null },
    { label: 'Classico', value: 'classico' },
    { label: 'Premium', value: 'premium' }
  ];
  // B4A-6675: diferente dos dois acima, não tem opção "todos"/null — a coluna é
  // bit NOT NULL, então só existe Não (default) e Sim.
  brindeOptions: Array<{ label: string; value: boolean }> = [
    { label: 'Não', value: false },
    { label: 'Sim', value: true }
  ];
  isKitOptions: Array<{ label: string; value: boolean }> = [
    { label: 'Não', value: false },
    { label: 'Sim', value: true }
  ];
  kitSuggestions: Array<ProductVariantSummary> = [];
  kitComponentToAdd = new FormControl<ProductVariantSummary | null>(null);
  kitQuantityToAdd = new FormControl<number>(1, [Validators.min(1)]);
  isKitSelected = false;
  generoOptions = [
    { label: 'Unisex', value: 'U' },
    { label: 'Feminino', value: 'F' },
    { label: 'Masculino', value: 'M' }
  ];

  override form = new FormGroup({
    productVariantId: new FormControl<number>(
      {
        value: null,
        disabled: true
      },
      Validators.required
    ),
    productId: new FormControl<number>(null, Validators.required),
    productVariantName: new FormControl<string>(null, [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(255)
    ]),
    costPrice: new FormControl<number>(0),
    allocationCost: new FormControl<number>(0),
    qtdItens: new FormControl<number>(null, [Validators.min(1)]),
    invoicePrice: new FormControl<number>(null),
    invoiceName: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    unitName: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(50)
    ]),
    unitMeasure: new FormControl<number>(null, [
      Validators.required,
      Validators.min(1)
    ]),
    weight: new FormControl<number>(null, Validators.required),
    salePrice: new FormControl<number>(0),
    saleStock: new FormControl<number>(0),
    isSellable: new FormControl<boolean>(false),
    tradePoints: new FormControl<number>(null),
    maxTradesPerPerson: new FormControl<number>(0),
    tradeActive: new FormControl<boolean>(false),
    isTradeable: new FormControl<boolean>(false),
    defaultImageURL: new FormControl<string>(null),
    externalStoreUrl: new FormControl<string>(null),
    longExternalStoreUrl: new FormControl<string>(null),
    isSellableUrl: new FormControl<boolean>(null),
    wmsId: new FormControl<number>(null),
    displayOrder: new FormControl<number>(null),
    internalEAN: new FormControl<string>(null, [Validators.maxLength(15)]),
    externalId: new FormControl<number>(null),
    cean: new FormControl<string>(null, Validators.maxLength(14)),
    extipi: new FormControl<string>(null, Validators.maxLength(14)),
    ncm: new FormControl<string>(null, Validators.maxLength(50)),
    cst: new FormControl<string>(null, Validators.maxLength(50)),
    cfop: new FormControl<string>(null, Validators.maxLength(50)),
    genero: new FormControl<string>('U'),
    diamanteVermelho: new FormControl<{ label: string; value: string | null }>(
      this.diamanteVermelhoOptions[0]
    ),
    tipoEstoque: new FormControl<{ label: string; value: string | null }>(
      this.tipoEstoqueOptions[0]
    ),
    brinde: new FormControl<{ label: string; value: boolean }>(
      this.brindeOptions[0]
    ),
    isKit: new FormControl<{ label: string; value: boolean }>(
      this.isKitOptions[0]
    ),
    kitComponents: new FormArray<FormGroup>([]),
    expirationAttribute: new FormControl<string>(null)
  });

  constructor(
    private productService: ProductControllerService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    override router: Router,
    override fileService: FileControllerService
  ) {
    super(fileService, router);
  }

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    if (this.product) {
      this.form.controls.productVariantId.enable();
      const { kitComponents, ...productValue } = this.product;
      this.form.patchValue({
        ...productValue,
        expirationAttribute: this.product.expiration,
        diamanteVermelho: this.resolveDiamanteVermelhoOption(
          this.product.diamanteVermelho
        ),
        tipoEstoque: this.resolveTipoEstoqueOption(this.product.tipoEstoque),
        brinde: this.resolveBrindeOption(this.product.brinde),
        isKit: this.resolveIsKitOption(this.product.isKit)
      });
      this.replaceKitComponents(kitComponents || []);
      this.onIsKitChange();
    } else {
      this.form.controls.productId.setValue(this.productId);
    }
    LoaderService.showLoader(false);
  }

  private resolveDiamanteVermelhoOption(
    value?: string | null
  ): { label: string; value: string | null } {
    return (
      this.diamanteVermelhoOptions.find(
        (option) => option.value === (value ?? null)
      ) ?? this.diamanteVermelhoOptions[0]
    );
  }

  private resolveBrindeOption(
    value?: boolean | null
  ): { label: string; value: boolean } {
    // Variante antiga (antes da coluna existir) chega sem valor: cai em "Não".
    return this.brindeOptions.find((option) => option.value === value) ??
      this.brindeOptions[0];
  }

  private resolveIsKitOption(
    value?: boolean | null
  ): { label: string; value: boolean } {
    return this.isKitOptions.find((option) => option.value === value) ??
      this.isKitOptions[0];
  }

  get kitComponents(): FormArray<FormGroup> {
    return this.form.controls['kitComponents'] as FormArray<FormGroup>;
  }

  onIsKitChange(): void {
    this.isKitSelected = !!this.form.controls['isKit'].value?.value;
    if (!this.isKitSelected) {
      this.kitComponents.clear();
    }
    this.syncKitQuantityState();
  }

  private replaceKitComponents(components: Array<ProductKitComponent>): void {
    this.kitComponents.clear();
    for (const component of components) {
      this.kitComponents.push(this.buildKitComponentGroup(component));
    }
  }

  private buildKitComponentGroup(component: ProductKitComponent): FormGroup {
    return new FormGroup({
      productVariantId: new FormControl<number>(component.productVariantId, Validators.required),
      quantity: new FormControl<number>(component.quantity || 1, [
        Validators.required,
        Validators.min(1)
      ]),
      productVariantName: new FormControl<string>(component.productVariantName),
      millenniumSKU: new FormControl<string>(component.millenniumSKU),
      internalEAN: new FormControl<string>(component.internalEAN)
    });
  }

  async searchKitComponents(event: { query: string }): Promise<void> {
    const search = event?.query?.trim();
    if (!search) {
      this.kitSuggestions = [];
      return;
    }
    try {
      const result = await lastValueFrom(
        this.productService
          .searchVariantsByNameOrExternalIdOrBrandOrInternalEAN(search)
          .pipe(map((data) => data.result || []))
      );
      const currentId = this.form.controls['productVariantId'].value;
      const selectedIds = this.kitComponents.controls.map(
        (control) => control.value.productVariantId
      );
      this.kitSuggestions = result.filter(
        (variant) =>
          variant.productVariantId !== currentId &&
          !selectedIds.includes(variant.productVariantId)
      );
    } catch (error: any) {
      this.kitSuggestions = [];
      AppDialogService.showErrorDialog(error);
    }
  }

  addKitComponent(): void {
    const selected = this.kitComponentToAdd.value;
    if (!selected?.productVariantId) {
      return;
    }
    const quantity = Number(this.kitQuantityToAdd.value);
    if (!quantity || quantity < 1) {
      return;
    }
    this.kitComponents.push(
      this.buildKitComponentGroup({
        productVariantId: selected.productVariantId,
        quantity,
        productVariantName: selected.productVariantName,
        internalEAN: selected.internalEAN
      })
    );
    this.kitComponentToAdd.setValue(null);
    this.kitQuantityToAdd.setValue(1);
    this.syncKitQuantityState();
  }

  removeKitComponent(index: number): void {
    this.kitComponents.removeAt(index);
    this.syncKitQuantityState();
  }

  onKitQuantityChange(): void {
    this.syncKitQuantityState();
  }

  private syncKitQuantityState(): void {
    if (this.isKitSelected) {
      const total = this.kitComponents.controls.reduce((sum, control) => {
        const quantity = Number(control.value.quantity);
        return sum + (quantity > 0 ? quantity : 0);
      }, 0);
      this.form.controls['qtdItens'].setValue(total > 0 ? total : 1);
      this.form.controls['qtdItens'].disable({ emitEvent: false });
    } else {
      this.form.controls['qtdItens'].enable({ emitEvent: false });
    }
  }

  private resolveTipoEstoqueOption(
    value?: string | null
  ): { label: string; value: string | null } {
    return (
      this.tipoEstoqueOptions.find((option) => option.value === (value ?? null)) ??
      this.tipoEstoqueOptions[0]
    );
  }

  private buildSubmitPayload():
    | ProductVariantCreateRequest
    | ProductVariantUpdateRequest {
    const { tipoEstoque, diamanteVermelho, brinde, isKit, kitComponents, ...formValue } =
      this.form.getRawValue();

    return {
      ...formValue,
      diamanteVermelho: diamanteVermelho?.value ?? null,
      tipoEstoque: tipoEstoque?.value ?? null,
      brinde: brinde?.value ?? false,
      isKit: isKit?.value ?? false,
      kitComponents: isKit?.value
        ? (kitComponents || []).map((component) => ({
            productVariantId: component['productVariantId'],
            quantity: component['quantity']
          }))
        : []
    } as ProductVariantCreateRequest | ProductVariantUpdateRequest;
  }

  changeTradeable(): void {
    this.form?.controls['isTradeable'].setValue(this.form.value.tradeActive);
    if (this.form?.value.tradeActive) {
      this.form?.controls['tradePoints'].setValidators([
        Validators.required,
        Validators.min(1)
      ]);
      this.form?.controls['maxTradesPerPerson'].setValidators([
        Validators.required,
        Validators.min(1)
      ]);
      this.form?.controls['maxTradesPerPerson'].setValue(
        this.form?.value.maxTradesPerPerson || 1
      );
    } else {
      this.form?.controls['tradePoints'].clearValidators();
      this.form?.controls['maxTradesPerPerson'].clearValidators();
    }
    this.form?.controls['tradePoints'].updateValueAndValidity();
    this.form?.controls['maxTradesPerPerson'].updateValueAndValidity();
  }

  onTradeActiveChange(): void {
    if (
      this.form?.value.tradeActive &&
      this.model &&
      (!this.model.inStockFidelization ||
        this.model.inStockFidelization <= this.minStock)
    ) {
      this.confirmationService.confirm({
        acceptLabel: 'Sim',
        rejectLabel: 'Não',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-primary',
        closeOnEscape: false,
        message: `Estoque da variante: ${
          this.model.inStockFidelization
        }<br> Estoque mínimo exigido: ${
          this.minStock + 1
        }<br>O produto <b>NÃO</b> será exibido na vitrine!<br>Deseja continuar?`,
        accept: () => {
          this.changeTradeable();
        },
        reject: () => {
          this.form?.controls['tradeActive'].setValue(false);
        }
      });
    } else {
      this.changeTradeable();
    }
  }

  async submit(): Promise<void> {
    FormUtil.trimAllStrings(this.form);
    if (this.form?.valid) {
      if (this.isKitSelected && this.kitComponents.length === 0) {
        this.messageService.add({
          severity: 'error',
          detail: 'Kit deve ter ao menos um componente.',
          summary: 'Erro'
        });
        return;
      }
      LoaderService.showLoader();
      try {
        if (this.product?.productVariantId) {
          this.product = await lastValueFrom(
            this.productService
              .updateProductVariant(
                this.buildSubmitPayload() as ProductVariantUpdateRequest
              )
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            severity: 'success',
            detail: 'Variante atualizada com sucesso.',
            summary: 'Sucesso'
          });
        } else {
          this.product = await lastValueFrom(
            this.productService
              .createProductVariant(
                this.buildSubmitPayload() as ProductVariantCreateRequest
              )
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            severity: 'success',
            detail: 'Variante cadastrada com sucesso.',
            summary: 'Sucesso'
          });
        }
        await this.saveProductImages();
        this.buttonAction.emit(this.product);
      } catch (error: any) {
        LoaderService.showLoader(false);
        AppDialogService.showErrorDialog(error);
      }
    }
  }

  imageVariant(productVariantId?: number): string {
    return (
      this.productVariants?.find((p) => p.productVariantId === productVariantId)
        ?.productVariantName || 'Sem variante'
    );
  }

  async saveProductImages(): Promise<void> {
    if (this.filesUploaded.length) {
      const images: Array<ProductImageRequest> = [];
      for (let index = 0; index < this.filesUploaded.length; index++) {
        images.push({
          imageUrl: this.filesUploaded[index],
          position: index,
          productId: this.productId,
          productVariantId: this.product?.productVariantId
        });
      }
      try {
        await Promise.all(
          images.map((i) =>
            lastValueFrom(this.productService.addProductImage(i))
          )
        );
        await this.afterSubmit();
      } catch (error: any) {
        AppDialogService.showErrorDialog(error);
      }
    }
  }

  get filePath(): string {
    return '/admin/products/';
  }

  override get model() {
    return this.product;
  }

  override get modelId() {
    return this.product?.productId || 0;
  }
}
